import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { CartDrawer } from "@/components/cart-drawer";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const links = [
  { label: "Perfumes", href: "/products", route: true },
  { label: "How It Works", href: "/how-it-works", route: true },
  { label: "Field Notes", href: "/field-notes", route: true },
  { label: "About", href: "/about", route: true },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-border bg-background/85 backdrop-blur-md py-3"
          : "border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6">
        {/* Mobile menu toggle */}
        <div className="flex flex-1 md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="eyebrow text-foreground/70 transition-colors hover:text-gold"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[80vw] max-w-sm bg-background border-r border-border">
              <SheetHeader>
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              </SheetHeader>
              <nav className="mt-8 flex flex-col gap-6">
                {links.map((l) => (
                  <SheetClose asChild key={l.href}>
                    <NavLinkMobile {...l} />
                  </SheetClose>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        <nav className="hidden flex-1 items-center gap-8 md:flex">
          {links.slice(0, 2).map((l) => (
            <NavLink key={l.href} {...l} />
          ))}
        </nav>

        <Link to="/" className="flex flex-col items-center leading-none" aria-label="House of Apocrypha — Home">
          <span className="font-[var(--font-logo)] text-[0.65rem] tracking-[0.35em] text-gold uppercase">
            House of
          </span>
          <span className="font-[var(--font-logo)] text-xl tracking-[0.18em] text-parchment uppercase sm:text-2xl">
            Apocrypha
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-end gap-8 md:flex">
          {links.slice(2).map((l) => (
            <NavLink key={l.href} {...l} />
          ))}
          <CartDrawer />
        </nav>

        <div className="flex flex-1 justify-end md:hidden">
          <CartDrawer />
        </div>
      </div>
    </header>
  );
}

function NavLink({ label, href, route }: { label: string; href: string; route?: boolean }) {
  const className = "eyebrow text-foreground/70 transition-colors hover:text-gold";
  if (route) {
    return (
      <Link to={href} className={className}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {label}
    </a>
  );
}

function NavLinkMobile({ label, href, route }: { label: string; href: string; route?: boolean }) {
  const className = "font-[var(--font-serif)] text-2xl text-foreground/90 transition-colors hover:text-gold text-left";
  if (route) {
    return (
      <Link to={href} className={className}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {label}
    </a>
  );
}
